import { z } from 'zod'

import type { MarketRange } from '@/features/market/api/marketApi'
import { codeParamSchema } from '@/schemas/common'

/**
 * Trading Average and Stock Trading read one stock's sessions, so they share
 * one URL: the company, and the period as either a preset or a from/to pair.
 *
 * The presets are the ranges `/market/stocks/{symbol}/price-history/` serves
 * (nothing else is faked by slicing a longer series). A custom from/to is
 * answered from the `ALL` series cut to those days, which is every real
 * session in them and no others.
 */
const TRADING_RANGES = ['1W', '1M', '3M', '1Y', 'ALL'] as const satisfies readonly MarketRange[]

export const DEFAULT_TRADING_RANGE: MarketRange = '1M'

const isoDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const tradingSearchSchema = z.object({
  /** Upper-cased: NEPSE tickers are, and the price-history key is case-sensitive. */
  symbol: codeParamSchema
    .transform((symbol) => symbol.toUpperCase())
    .optional()
    .catch(undefined),
  range: z.enum(TRADING_RANGES).default(DEFAULT_TRADING_RANGE).catch(DEFAULT_TRADING_RANGE),
  from: isoDaySchema.optional().catch(undefined),
  to: isoDaySchema.optional().catch(undefined),
})

export type TradingSearch = z.infer<typeof tradingSearchSchema>

/**
 * The one parse the loader and the screen read `search` through: the router
 * merges the raw query string into `Route.useSearch()`, so a stray param
 * would otherwise key the screen's read off a different object than the one
 * the loader warmed.
 */
export const toTradingSearch = (search: unknown): TradingSearch => tradingSearchSchema.parse(search)

/** The period on screen: a preset, or a custom pair of days (earlier first). */
export type TradingPeriod =
  { kind: 'preset'; range: MarketRange } | { kind: 'custom'; from: string; to: string }

/** A pair in the wrong order is read the right way round, not dropped. */
export const periodOf = (search: TradingSearch): TradingPeriod => {
  const { from, to, range } = search
  if (from !== undefined && to !== undefined) {
    return from <= to ? { kind: 'custom', from, to } : { kind: 'custom', from: to, to: from }
  }
  return { kind: 'preset', range }
}

/** What to ask mm-be for: the preset itself, or the whole history for a custom window. */
export const fetchRangeOf = (period: TradingPeriod): MarketRange =>
  period.kind === 'preset' ? period.range : 'ALL'
